// File: frontend/src/actions/profileActions.js

import {
  CREATE_PROFILEINFO_REQUEST,
  CREATE_PROFILEINFO_SUCCESS,
  CREATE_PROFILEINFO_FAIL,

  CREATE_PROFILEMEDIA_REQUEST,
  CREATE_PROFILEMEDIA_SUCCESS,
  CREATE_PROFILEMEDIA_FAIL,

  FETCH_PROFILEINFO_REQUEST,
  FETCH_PROFILEINFO_SUCCESS,
  FETCH_PROFILEINFO_FAIL,

  UPDATE_PROFILEINFO_REQUEST,
  UPDATE_PROFILEINFO_SUCCESS,
  UPDATE_PROFILEINFO_FAIL,

  UPDATE_PROFILEMEDIA_REQUEST,
  UPDATE_PROFILEMEDIA_SUCCESS,
  UPDATE_PROFILEMEDIA_FAIL,

  FETCH_PROFILEMEDIA_REQUEST,
  FETCH_PROFILEMEDIA_SUCCESS,
  FETCH_PROFILEMEDIA_FAIL

} from './types';

import { resolveApiUrl } from '../utils/apiUrls';
import { getProfileMediaApiBase } from '../utils/profileMediaApi';

const USER_API = resolveApiUrl(process.env.REACT_APP_USER_API, 'http://localhost:2001/api/users', 'USER_API');
const DEFAULT_PROFILE_SLOT_BASE = '/api/user-media-profile/mediaprofile/default';

/** Chemins par défaut alignés sur user-backend (usersCtrl.register). */
const DEFAULT_PROFILE_SLOT_PATHS = [
  `${DEFAULT_PROFILE_SLOT_BASE}/slot-0.png`,
  `${DEFAULT_PROFILE_SLOT_BASE}/slot-1.png`,
  `${DEFAULT_PROFILE_SLOT_BASE}/slot-2.png`,
  `${DEFAULT_PROFILE_SLOT_BASE}/slot-3.png`,
];
const PROFILE_SLOT_INDICES = [0, 1, 2, 3];

function parseSlotIndex(value) {
  const n = Number(value);
  return Number.isInteger(n) ? n : null;
}

function isCanonicalProfileSlot(slot) {
  return PROFILE_SLOT_INDICES.includes(slot);
}

function slotTimestampValue(item) {
  const raw = item?.updatedAt || item?.createdAt || null;
  const ts = raw ? Date.parse(raw) : NaN;
  return Number.isFinite(ts) ? ts : 0;
}

function numericIdValue(item) {
  const n = Number(item?.id);
  return Number.isFinite(n) ? n : 0;
}

function isDefaultPathForSlot(path, slot) {
  const value = String(path || '');
  return value === DEFAULT_PROFILE_SLOT_PATHS[slot] || value === `/mediaprofile/default/slot-${slot}.png`;
}

function pickBestRecordForSlot(records, slot) {
  if (!Array.isArray(records) || records.length === 0) return null;
  const ranked = [...records].sort((a, b) => {
    const aCustom = isDefaultPathForSlot(a?.path, slot) ? 0 : 1;
    const bCustom = isDefaultPathForSlot(b?.path, slot) ? 0 : 1;
    if (aCustom !== bCustom) return bCustom - aCustom;

    const byTs = slotTimestampValue(b) - slotTimestampValue(a);
    if (byTs !== 0) return byTs;

    return numericIdValue(b) - numericIdValue(a);
  });
  return ranked[0] || null;
}

function normalizeToFourProfileSlots(rawSlots) {
  const slots = Array.isArray(rawSlots) ? rawSlots : [];
  const grouped = new Map(PROFILE_SLOT_INDICES.map((s) => [s, []]));

  for (const item of slots) {
    const slot = parseSlotIndex(item?.slot);
    if (slot == null || !isCanonicalProfileSlot(slot)) continue;
    grouped.get(slot).push(item);
  }

  return PROFILE_SLOT_INDICES.map((slot) => {
    const best = pickBestRecordForSlot(grouped.get(slot), slot);
    if (best) return best;
    return {
      id: `default-slot-${slot}`,
      slot: slot,
      path: DEFAULT_PROFILE_SLOT_PATHS[slot],
      type: '',
      filename: '',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
  });
}

function normalizeProfileMediaList(data) {
  const slots = Array.isArray(data)
    ? data
    : (data?.slots ??
      data?.media ??
      data?.data ??
      data?.items ??
      data?.results ??
      data?.mediaProfiles ??
      data?.list ??
      []);
  return Array.isArray(slots) ? slots : [];
}

/** Indices 0–3 absents de la réponse API (profil sans provisionnement média, ex. ancien script SQL). */
function missingProfileSlotIndices(slots) {
  return PROFILE_SLOT_INDICES.filter((i) => !slots.some((s) => parseSlotIndex(s?.slot) === i));
}

async function postDefaultProfileSlot(profileId, slot) {
  const base = getProfileMediaApiBase();
  const res = await fetch(`${base}/mediaProfile/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      profileId,
      filename: '',
      path: DEFAULT_PROFILE_SLOT_PATHS[slot],
      type: '',
      slot,
    }),
  });
  if (!res.ok) {
    const errBody = await res.json().catch(() => ({}));
    throw new Error(
      errBody.error || errBody.details || `Provisionnement slot ${slot} : HTTP ${res.status}`
    );
  }
}

async function ensureProfileMediaSlots(profileId, slots) {
  const missing = missingProfileSlotIndices(slots);
  if (missing.length === 0) return slots;
  console.warn(
    '[fetchProfileMedia] Emplacements manquants, provisionnement:',
    missing,
    '(profileId=',
    profileId,
    ')'
  );
  for (const slot of missing) {
    await postDefaultProfileSlot(profileId, slot);
  }
  const base = getProfileMediaApiBase();
  const url = `${base}/mediaProfile/${profileId}?ts=${Date.now()}`;
  const token = localStorage.getItem('accessToken');
  const res = await fetch(url, {
    method: 'GET',
    cache: 'no-store',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Erreur relecture médias après provisionnement');
  return normalizeProfileMediaList(data);
}



export const fetchProfileInfo = (id) => async (dispatch) => {
  dispatch({ type: FETCH_PROFILEINFO_REQUEST });
  const token = localStorage.getItem('accessToken');

  if (!token) {
    dispatch({ type: FETCH_PROFILEINFO_FAIL, payload: 'Token manquant' });
    return;
  }

  try {
    const response = await fetch(`${USER_API}/infoProfile/user`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Erreur récupération profil');

    dispatch({ type: FETCH_PROFILEINFO_SUCCESS, payload: data });
  } catch (error) {
    dispatch({ type: FETCH_PROFILEINFO_FAIL, payload: error.message });
  }
}

export const createFullProfile = ({ profileInfoCreate = {}, profileMediaCreate = [] }) => async (dispatch) => {
  // 🔹 Création du profil utilisateur
  if (Object.keys(profileInfoCreate).length > 0) {
    dispatch({ type: CREATE_PROFILEINFO_REQUEST });
    try {
      const response = await fetch(`${USER_API}/infoProfile/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(profileInfoCreate)
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Erreur création profil');
      dispatch({ type: CREATE_PROFILEINFO_SUCCESS, payload: data });

      const profileId = data.id;

      // 🔹 Création des médias liés au profil
      if (profileMediaCreate.length > 0) {
        for (const media of profileMediaCreate) {
          dispatch({ type: CREATE_PROFILEMEDIA_REQUEST });
          try {
            const mediaResponse = await fetch(`${getProfileMediaApiBase()}/mediaProfile/`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ ...media, profileId })
            });
            const mediaData = await mediaResponse.json();
            if (!mediaResponse.ok) throw new Error(mediaData.error || 'Erreur création média');
            dispatch({ type: CREATE_PROFILEMEDIA_SUCCESS, payload: mediaData });
          } catch (error) {
            dispatch({ type: CREATE_PROFILEMEDIA_FAIL, payload: error.message });
          }
        }
      }
    } catch (error) {
      dispatch({ type: CREATE_PROFILEINFO_FAIL, payload: error.message });
    }
  }
};


export const updateProfileInfo = (id, formData) => async (dispatch) => {
  dispatch({ type: UPDATE_PROFILEINFO_REQUEST });

  const token = localStorage.getItem('accessToken'); // ✅ dynamique

  if (!token) {
    dispatch({ type: UPDATE_PROFILEINFO_FAIL, payload: 'Token manquant' });
    return;
  }

  try {
    const response = await fetch(`${USER_API}/infoProfile/${id}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(formData)
    });

    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Erreur mise à jour profil');

    dispatch({ type: UPDATE_PROFILEINFO_SUCCESS, payload: data });
  } catch (error) {
    dispatch({ type: UPDATE_PROFILEINFO_FAIL, payload: error.message });
  }
};


export const updateProfileMedia = (mediaId, payload) => async (dispatch) => {
  console.log('📤 Début updateProfileMedia pour mediaId :', mediaId);
  console.log('📦 Payload envoyé :', payload);

  dispatch({ type: UPDATE_PROFILEMEDIA_REQUEST });
  const token = localStorage.getItem('accessToken');

  if (!token) {
    console.error('❌ Token manquant');
    dispatch({ type: UPDATE_PROFILEMEDIA_FAIL, payload: 'Token manquant' });
    throw new Error('Token manquant');
  }

  const url = `${getProfileMediaApiBase()}/mediaProfile/${mediaId}`;
  console.log('🚀 Requête PUT vers :', url);

  try {
    const response = await fetch(url, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    console.log('📨 Réponse reçue du backend');
    const data = await response.json();
    console.log('📄 Contenu JSON :', data);

    if (!response.ok) {
      console.error('❌ Réponse non OK :', response.status);
      throw new Error(data.error || 'Erreur mise à jour média');
    }

    console.log('✅ Mise à jour réussie, dispatch UPDATE_PROFILEMEDIA_SUCCESS');
    dispatch({ type: UPDATE_PROFILEMEDIA_SUCCESS, payload: data });
    return data;
  } catch (error) {
    console.error('❌ Erreur updateProfileMedia :', error.message);
    dispatch({ type: UPDATE_PROFILEMEDIA_FAIL, payload: error.message });
    throw error;
  }
};


export const fetchProfileMedia = (profileId) => async (dispatch) => {
  dispatch({ type: FETCH_PROFILEMEDIA_REQUEST });
  const token = localStorage.getItem('accessToken');

  if (!token) {
    dispatch({ type: FETCH_PROFILEMEDIA_FAIL, payload: 'Token manquant' });
    return;
  }

  const base = getProfileMediaApiBase();
  const url = `${base}/mediaProfile/${profileId}?ts=${Date.now()}`;
  console.log('[fetchProfileMedia] GET', url, 'profileId=', profileId);

  try {
    const response = await fetch(url, {
      method: 'GET',
      cache: 'no-store',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Erreur récupération médias');

    console.log(
      '[fetchProfileMedia] Réponse brute:',
      JSON.stringify(data).slice(0, 500),
      '| type:',
      Array.isArray(data) ? 'array' : typeof data,
      Array.isArray(data) ? `length=${data.length}` : data ? `keys=[${Object.keys(data).join(', ')}]` : ''
    );

    let slots = normalizeProfileMediaList(data);

    slots = await ensureProfileMediaSlots(profileId, slots);

    slots = normalizeToFourProfileSlots(slots);

    slots.sort((a, b) => Number(a.slot) - Number(b.slot));
    console.log('[fetchProfileMedia] Slots finaux:', slots.length, slots);

    dispatch({ type: FETCH_PROFILEMEDIA_SUCCESS, payload: slots });
  } catch (error) {
    dispatch({ type: FETCH_PROFILEMEDIA_FAIL, payload: error.message });
  }
};
